import { zodResolver } from "@hookform/resolvers/zod";
import { useEffect, useMemo } from "react";
import { useFieldArray, useForm } from "react-hook-form";

import {
  useCreateRiderPayroll,
  useUpdateRiderPayroll,
} from "@/hooks/use-rider-payrolls";
import { newIdempotencyKey } from "@/lib/idempotency";

import {
  defaultRiderPayrollValues,
  emptyPayrollAdjustment,
  riderPayrollSchema,
  type RiderPayrollFormValues,
} from "./rider-payroll-schema";

import type { RiderPayroll } from "@artisancode/api-types";

export function useRiderPayrollForm(
  open: boolean,
  editing: RiderPayroll | null,
  onSuccess: () => void,
) {
  const { mutate: create, isPending: isCreating } = useCreateRiderPayroll();
  const { mutate: update, isPending: isUpdating } = useUpdateRiderPayroll();

  // One key per opened form: a retry after a network error reuses it, so the
  // server returns the payroll it already created instead of making a second.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const idempotencyKey = useMemo(() => newIdempotencyKey("rpr"), [open]);

  const isDraft = !editing || editing.status === "draft";
  const isSaving = isCreating || isUpdating;

  const form = useForm<RiderPayrollFormValues>({
    resolver: zodResolver(riderPayrollSchema),
    defaultValues: defaultRiderPayrollValues,
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "adjustments",
  });

  useEffect(() => {
    if (!open) return;
    if (editing) {
      form.reset({
        riderId: editing.riderId,
        // Populated shortly after by RiderPayrollPeriodPicker once it knows
        // which rider_sales are currently linked to this payroll.
        riderSaleIds: [],
        paymentMethod: editing.paymentMethod,
        adjustments: editing.adjustments,
        notes: editing.notes ?? "",
      });
    } else {
      form.reset(defaultRiderPayrollValues);
    }
  }, [open, editing, form]);

  function addAdjustment() {
    append(emptyPayrollAdjustment);
  }

  const onSubmit = form.handleSubmit((values) => {
    if (editing) {
      update(
        {
          id: editing.id,
          riderSaleIds: values.riderSaleIds,
          paymentMethod: values.paymentMethod,
          adjustments: values.adjustments,
          notes: values.notes,
        },
        { onSuccess },
      );
    } else {
      create(
        {
          body: {
            riderId: values.riderId,
            riderSaleIds: values.riderSaleIds,
            paymentMethod: values.paymentMethod,
            adjustments: values.adjustments,
            notes: values.notes,
          },
          idempotencyKey,
        },
        { onSuccess: () => onSuccess() },
      );
    }
  });

  return {
    form,
    fields,
    addAdjustment,
    removeAdjustment: remove,
    onSubmit,
    isSaving,
    isDraft,
  };
}
